import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react';
import { Issue, IssueFormData, IssueFilters } from '../types/issue';
import { issueService } from '../services/issueService';
import { toast } from 'react-hot-toast';

interface IssueContextType {
  issues: Issue[];
  loading: boolean;
  error: string | null;
  fetchIssues: (filters?: IssueFilters) => Promise<void>;
  createIssue: (data: IssueFormData) => Promise<Issue>;
  updateIssue: (id: number, data: IssueFormData) => Promise<Issue>;
  deleteIssue: (id: number) => Promise<void>;
  publishIssue: (id: number) => Promise<Issue>;
  archiveIssue: (id: number) => Promise<Issue>;
  getIssueById: (id: number) => Promise<Issue>;
  incrementDownloads: (id: number) => Promise<void>;
}

const IssueContext = createContext<IssueContextType | undefined>(undefined);

export const IssueProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchIssues = useCallback(async (filters?: IssueFilters) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Начало загрузки выпусков...');
      const data = await issueService.getAllIssues(filters);
      console.log('Получены выпуски:', data);
      setIssues(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка при загрузке выпусков';
      console.error('Ошибка при загрузке выпусков:', err);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const createIssue = useCallback(async (data: IssueFormData) => {
    try {
      setLoading(true);
      setError(null);
      const newIssue = await issueService.createIssue(data);
      setIssues(prev => [...prev, newIssue]);
      toast.success('Выпуск успешно создан');
      return newIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при создании выпуска');
      toast.error('Ошибка при создании выпуска');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateIssue = useCallback(async (id: number, data: IssueFormData) => {
    try {
      setLoading(true);
      setError(null);
      const updatedIssue = await issueService.updateIssue(id, data);
      setIssues(prev => prev.map(issue => issue.id === id ? updatedIssue : issue));
      toast.success('Выпуск успешно обновлен');
      return updatedIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при обновлении выпуска');
      toast.error('Ошибка при обновлении выпуска');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteIssue = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      await issueService.deleteIssue(id);
      setIssues(prev => prev.filter(issue => issue.id !== id));
      toast.success('Выпуск успешно удален');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при удалении выпуска');
      toast.error('Ошибка при удалении выпуска');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const publishIssue = async (id: number) => {
    try {
      const publishedIssue = await issueService.publishIssue(id);
      setIssues(prev => prev.map(issue => issue.id === id ? publishedIssue : issue));
      toast.success('Выпуск успешно опубликован');
      return publishedIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при публикации выпуска');
      toast.error('Ошибка при публикации выпуска');
      throw err;
    }
  };

  const archiveIssue = async (id: number) => {
    try {
      const archivedIssue = await issueService.archiveIssue(id);
      setIssues(prev => prev.map(issue => issue.id === id ? archivedIssue : issue));
      toast.success('Выпуск успешно архивирован');
      return archivedIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при архивации выпуска');
      toast.error('Ошибка при архивации выпуска');
      throw err;
    }
  };

  const getIssueById = async (id: number) => {
    try {
      return await issueService.getIssueById(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при получении выпуска');
      toast.error('Ошибка при получении выпуска');
      throw err;
    }
  };

  const incrementDownloads = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const updatedIssue = await issueService.incrementDownloads(id);
      setIssues(prev => prev.map(issue => issue.id === id ? updatedIssue : issue));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при обновлении счетчика скачиваний');
      toast.error('Ошибка при обновлении счетчика скачиваний');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(() => ({
    issues,
    loading,
    error,
    fetchIssues,
    createIssue,
    updateIssue,
    deleteIssue,
    publishIssue,
    archiveIssue,
    getIssueById,
    incrementDownloads,
  }), [issues, loading, error, fetchIssues, createIssue, updateIssue, deleteIssue, publishIssue, archiveIssue, getIssueById, incrementDownloads]);

  return (
    <IssueContext.Provider value={value}>
      {children}
    </IssueContext.Provider>
  );
};

export const useIssues = () => {
  const context = useContext(IssueContext);
  if (context === undefined) {
    throw new Error('useIssues must be used within an IssueProvider');
  }
  return context;
}; 